import { TranslatePipe } from '@ngx-translate/core';
import { ValidationErrors, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { ServiceStateEnum } from '@mwe/constants';
import {
  BIRTH_DATE_CONTROL_NAME,
  CARD_NUMBER_CONTROL_NAME,
  COMMIT_CONTROL_NAME,
  ErrorType,
  isDigitalWienerLinienVoucher,
  NewVoucherIdentifier,
  WL_ERRORS,
} from '../new-voucher.util';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ButtonComponent } from '../../buttons/button/button.component';
import { FormComponent } from '../../form/form.component';
import { mapToErrorItems } from '../../form/validators/map-error-key.validator';
import { NewVoucherStore, NewVoucherStoreType } from '../new-voucher.store';
import { createBirthDatePickerTimeRange } from '@mwe/utils';
import { Aktionscode, IFormModel } from '@mwe/models';
import { LoadingService } from '@mwe/services';

@Component({
  selector: 'mwe-new-wl-voucher-form',
  imports: [ButtonComponent, FormComponent, TranslatePipe],
  templateUrl: './new-wl-voucher-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewWlVoucherFormComponent implements OnInit {
  private readonly newVoucherStore = inject<NewVoucherStoreType>(NewVoucherStore);
  private readonly loadingService = inject(LoadingService);
  private readonly injector = inject(Injector);

  readonly wienerLinienVoucherDetail = input.required<Aktionscode>();
  readonly wienerLinienInfo = output<IFormModel>();

  readonly formRef = viewChild<FormComponent>('form');
  readonly checkBoxFormRef = viewChild<FormComponent>('checkBoxForm');

  readonly loading = toSignal<boolean>(
    this.loadingService.getLoadingState(NewVoucherIdentifier.VOUCHER_VALIDITY).pipe(map(state => state === ServiceStateEnum.LOADING)),
  );

  readonly formModel = signal<IFormModel>(undefined);
  readonly formData = signal<IFormModel>(undefined);
  readonly isFormValid = signal<boolean>(false);

  readonly checkBoxFormModel = signal<IFormModel>(undefined);
  readonly checkBoxFormData = signal<IFormModel>(undefined);
  readonly isCheckBoxFormValid = signal<boolean>(false);

  readonly marginWithSuccess = computed<boolean>(() => !!this.newVoucherStore.voucherValidity());
  readonly translate = computed(() => {
    return { digital: isDigitalWienerLinienVoucher(this.wienerLinienVoucherDetail().aktion?.aktionsArt) ? ' digitalen ' : ' ' };
  });

  /** ===== Initialization ===== **/

  ngOnInit(): void {
    this.formModel.set(this.initFormModel());
    this.checkBoxFormModel.set(this.initCheckBoxFormModel());
  }

  /** ===== Effects from store ===== **/

  constructor() {
    // Whenever an error occurs, set the error
    effect(() => {
      const errorCode = this.newVoucherStore.voucherValidityError()?.error?.code;
      if (!errorCode) return;

      untracked(() => this.newVoucherStore.updateVoucher(undefined));
      untracked(() => this.newVoucherStore.updateIsNewValid(false)); // otherwise infinite loop
      this.setFormError(errorCode);
    });

    effect(() => {
      const setWLError = this.newVoucherStore.setWLError(); // react to side effect
      if (setWLError === undefined) return;

      // when skipping to next steps instantly, this should be called after forms have been constructed
      afterNextRender(() => this.onClick(), { injector: this.injector });
    });
  }

  /** ===== HTML Functions ===== **/

  onFormChange(formData: IFormModel, validity: boolean): void {
    this.formData.set(formData);
    this.isFormValid.set(validity);
  }

  onCheckboxFormChange(formData: IFormModel): void {
    const value = formData?.[COMMIT_CONTROL_NAME];

    this.checkBoxFormData.set(formData);
    this.isCheckBoxFormValid.set(value ?? false);
    // without this unchecking the box will not result to an error
    if (value === false) this.checkBoxFormRef().form.get(COMMIT_CONTROL_NAME).setValue('');
  }

  onClick(): void {
    this.formRef().fireValidation();
    this.checkBoxFormRef().fireValidation();

    if (!(this.isFormValid() && this.isCheckBoxFormValid())) return;

    this.wienerLinienInfo.emit({
      ...this.formData(),
      ...this.checkBoxFormData(),
    } as IFormModel);
  }

  /** ===== Other Functions ===== **/

  setFormError(errorCode: ErrorType): void {
    const error = { [errorCode]: true } as ValidationErrors;
    this.formRef()?.setError(CARD_NUMBER_CONTROL_NAME, error);
  }

  /** ===== Signal Functions ===== **/

  initFormModel(): IFormModel {
    const birthdayTimeRange = createBirthDatePickerTimeRange();

    return {
      inputs: [
        {
          name: BIRTH_DATE_CONTROL_NAME,
          initialValue: '',
          labelKey: 'newVoucher.wl.inputBirthday.placeholder',
          validate: true,
          validators: [Validators.required],
          validationErrorLabelKey: 'newVoucher.wl.inputBirthday.error',
          validationErrorLabelParams: { ...birthdayTimeRange },
          componentType: 'birthdate',
          datepickerConfig: {
            minDate: birthdayTimeRange[0],
            maxDate: birthdayTimeRange[1],
            readonly: true,
            displayFormat: 'DD.MM.YYYY',
          },
        },
        {
          name: CARD_NUMBER_CONTROL_NAME,
          initialValue: '',
          labelKey: 'newVoucher.wl.inputCardNumber.placeholder',
          validate: true,
          validators: [Validators.required, Validators.maxLength(10), Validators.pattern(/^[0-9]{10}$/)],
          validationErrors: mapToErrorItems(WL_ERRORS),
          maxLength: 10,
          dataType: 'text',
          directiveType: 'integer',
          updateOn: 'blur',
        },
      ],
    };
  }

  initCheckBoxFormModel(): IFormModel {
    return {
      inputs: [
        {
          name: COMMIT_CONTROL_NAME,
          initialValue: '',
          placeholder: 'newVoucher.wl.inputCommit.text',
          validate: true,
          validators: [Validators.required],
          validationErrorLabelKey: 'newVoucher.wl.inputCommit.error',
          componentType: 'checkbox',
        },
      ],
    };
  }
}
